import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { 
  BWCFeeScheduleVersion, 
  BWCFeeScheduleEntry, 
  BWCFeeScheduleFilters,
  BWCFeeValidationResult 
} from '@/lib/bwc/types';

export function useBWCFeeSchedule() {
  const [versions, setVersions] = useState<BWCFeeScheduleVersion[]>([]);
  const [entries, setEntries] = useState<BWCFeeScheduleEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchVersions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: queryError } = await supabase
        .from('bwc_fee_schedule_versions')
        .select('*')
        .order('effective_date', { ascending: false });
      
      if (queryError) throw queryError;
      setVersions((data as BWCFeeScheduleVersion[]) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch versions');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEntries = useCallback(async (filters: BWCFeeScheduleFilters = {}) => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('bwc_fee_schedule_entries')
        .select(`
          *,
          version:bwc_fee_schedule_versions(version, effective_date, is_active)
        `);

      // Apply filters
      if (filters.cpt_codes && filters.cpt_codes.length > 0) {
        query = query.in('cpt_code', filters.cpt_codes);
      }
      if (filters.modifier) {
        query = query.eq('modifier', filters.modifier);
      }
      if (filters.coverage_flag) {
        query = query.eq('coverage_flag', filters.coverage_flag);
      }
      if (filters.min_fee !== undefined) {
        query = query.gte('non_facility_fee', filters.min_fee);
      }
      if (filters.max_fee !== undefined) {
        query = query.lte('non_facility_fee', filters.max_fee);
      }

      const { data, error: queryError } = await query.limit(500);
      
      if (queryError) throw queryError;
      setEntries((data as BWCFeeScheduleEntry[]) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch entries');
    } finally {
      setLoading(false);
    }
  }, []);

  const lookupFee = useCallback(async (
    cptCode: string, 
    modifier?: string,
    version: string = '2026'
  ): Promise<BWCFeeScheduleEntry | null> => {
    try {
      let query = supabase
        .from('bwc_fee_schedule_entries')
        .select(`
          *,
          version:bwc_fee_schedule_versions!inner(version, effective_date)
        `)
        .eq('cpt_code', cptCode)
        .eq('bwc_fee_schedule_versions.version', version);

      if (modifier) {
        query = query.eq('modifier', modifier);
      } else {
        query = query.is('modifier', null);
      }

      const { data, error: queryError } = await query.maybeSingle();
      
      if (queryError) throw queryError;
      return data as BWCFeeScheduleEntry | null;
    } catch (err) {
      console.error('Fee lookup error:', err);
      return null;
    }
  }, []);

  const searchCPTCodes = useCallback(async (searchTerm: string, limit = 50) => {
    if (!searchTerm || searchTerm.length < 2) return [];
    
    try {
      const { data, error: queryError } = await supabase
        .from('bwc_fee_schedule_entries')
        .select('cpt_code, modifier, non_facility_fee, facility_fee, coverage_flag')
        .ilike('cpt_code', `${searchTerm}%`)
        .limit(limit);
      
      if (queryError) throw queryError;
      return data || [];
    } catch (err) {
      console.error('CPT search error:', err);
      return [];
    }
  }, []);

  // Validate against official BWC lookup (external API call would go here)
  const validateAgainstOfficial = useCallback(async (
    cptCode: string,
    modifier?: string
  ): Promise<BWCFeeValidationResult> => {
    const internalEntry = await lookupFee(cptCode, modifier);
    
    // In production, this would call the BWC official API
    // For now, return a pending status indicating manual validation needed
    return {
      cpt_code: cptCode,
      modifier,
      internal_non_facility_fee: internalEntry?.non_facility_fee ?? undefined,
      internal_facility_fee: internalEntry?.facility_fee ?? undefined,
      match_status: internalEntry ? 'pending' : 'not_in_internal',
    };
  }, [lookupFee]);

  return {
    versions,
    entries,
    loading,
    error,
    fetchVersions,
    fetchEntries,
    lookupFee,
    searchCPTCodes,
    validateAgainstOfficial,
  };
}
